import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import { getSeenThreadSummaries } from "@sharpana/auth/api_client/summarization_api";
import { SummarizationMode } from "../../stores/shared_types";
import * as Sentry from "@sentry/react";
import { LoadingCircle } from "@sharpana/ui/LoadingCircle";

interface SummaryNavigationArrowsProps {
  resourceId?: string;
  summarizationMode: SummarizationMode;
  currentSlug?: string;
  contentLoaded?: boolean; // Trigger refetch when content finishes loading
}

export const SummaryNavigationArrows: React.FC<SummaryNavigationArrowsProps> = ({
  resourceId,
  summarizationMode,
  currentSlug,
  contentLoaded,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [slugs, setSlugs] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [loading, setLoading] = useState(false);

  // Fetch seen summaries from the API
  const fetchSeenSummaries = useCallback(async () => {
    if (!resourceId || !summarizationMode) return;

    try {
      setLoading(true);
      const response = await getSeenThreadSummaries(resourceId, summarizationMode);
      setSlugs(response.slugs || []);
    } catch (error) {
      // Set to empty array on error (e.g., authentication required for anonymous users)
      setSlugs([]);
      console.error("Failed to fetch seen summaries:", error);
      Sentry.captureException(error, {
        tags: {
          component: "SummaryNavigationArrows",
          resource_id: resourceId,
          mode: summarizationMode,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [resourceId, summarizationMode]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchSeenSummaries();
  }, [fetchSeenSummaries]);

  // Refetch when content finishes loading (post-regeneration)
  // This ensures the backend has created the GeneratedInteractionView before we fetch
  const previousContentLoadedRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    // Only refetch when contentLoaded transitions from false to true
    if (contentLoaded && previousContentLoadedRef.current === false) {
      // Small delay to ensure backend has saved the view
      const timeoutId = setTimeout(() => {
        fetchSeenSummaries();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    previousContentLoadedRef.current = contentLoaded;
  }, [contentLoaded, fetchSeenSummaries]);

  // Update current index when slugs or currentSlug changes
  useEffect(() => {
    if (slugs.length > 0 && currentSlug) {
      const index = slugs.indexOf(currentSlug);
      setCurrentIndex(index);
    } else {
      setCurrentIndex(-1);
    }
  }, [slugs, currentSlug]);

  // Navigate to a different slug
  const navigateToSlug = (slug: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("s", slug);
      return params;
    }, { replace: false }); // Use push navigation so user can go back
    // No reload needed - ThreadMode will detect the slug change and refetch
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      navigateToSlug(slugs[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (currentIndex >= 0 && currentIndex < slugs.length - 1) {
      navigateToSlug(slugs[currentIndex + 1]);
    }
  };

  // Show loading indicator when content is being regenerated
  const isRegenerating = contentLoaded === false && slugs.length > 0;
  
  // Don't render if we don't have the required data or only have one summary
  // BUT always show if we're regenerating (to display the loading indicator)
  if (!resourceId || (slugs.length <= 1 && !isRegenerating) || (currentIndex === -1 && !isRegenerating)) {
    return null;
  }

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < slugs.length - 1;

  return (
    <div css={containerStyle}>
      {isRegenerating && (
        <div css={loadingContainerStyle}>
          <LoadingCircle inline hideLabel size={14} variant="gray" />
        </div>
      )}
      {!isRegenerating && (
        <>
          <button
            css={arrowButtonStyle(hasPrevious)}
            onClick={handlePrevious}
            disabled={!hasPrevious}
            aria-label="Previous summary"
            title="Previous summary"
          >
            <ArrowLeftOutlined />
          </button>
          
          <span css={indexLabelStyle}>
            {currentIndex + 1} / {slugs.length}
          </span>
          
          <button
            css={arrowButtonStyle(hasNext)}
            onClick={handleNext}
            disabled={!hasNext}
            aria-label="Next summary"
            title="Next summary"
          >
            <ArrowRightOutlined />
          </button>
        </>
      )}
    </div>
  );
};

const containerStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
  min-width: 120px; /* Reserve space to prevent layout shift */
`;

const loadingContainerStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  color: #f2f2f2;
`;

const arrowButtonStyle = (enabled: boolean) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: ${enabled ? "pointer" : "not-allowed"};
  background: ${enabled ? "rgba(70, 70, 70, 0.4)" : "rgba(50, 50, 50, 0.2)"};
  border: 1px solid ${enabled ? "#3b3b3b" : "#2b2b2b"};
  color: ${enabled ? "#f2f2f2" : "#666666"};
  transition: background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
  opacity: ${enabled ? 1 : 0.5};
  
  &:hover {
    background: ${enabled ? "rgba(90, 90, 90, 0.5)" : "rgba(50, 50, 50, 0.2)"};
    border-color: ${enabled ? "#4b4b4b" : "#2b2b2b"};
  }

  &:active {
    background: ${enabled ? "rgba(60, 60, 60, 0.6)" : "rgba(50, 50, 50, 0.2)"};
  }
`;

const indexLabelStyle = css`
  font-size: 14px;
  color: #f2f2f2;
  font-weight: 500;
  min-width: 45px;
  text-align: center;
  user-select: none;
`;
